import React, { useState } from 'react';
import { Typography, Box, Stack, Chip, Grid, IconButton } from '@mui/material';
import { 
  Download as DownloadIcon, 
  Add as AddIcon, 
  Edit as EditIcon,
  Delete as DeleteIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import LiveEditor from '../components/Playground/LiveEditor';
import AutobiaButton from '../components/Buttons/AutobiaButton';

const Buttons = () => {
  const [loading, setLoading] = useState(false);

  // Button Examples
  const BasicButtonsExample = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <AutobiaButton variant="contained" color="primary" fullWidth>
          Primary Button
        </AutobiaButton>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <AutobiaButton variant="contained" color="secondary" fullWidth>
          Secondary Button
        </AutobiaButton>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <AutobiaButton variant="outlined" color="primary" fullWidth>
          Outlined Button
        </AutobiaButton>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <AutobiaButton variant="text" color="primary" fullWidth>
          Text Button
        </AutobiaButton>
      </Grid>
    </Grid>
  );

  const ButtonSizesExample = () => (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
      <AutobiaButton size="small" variant="contained">
        Small
      </AutobiaButton>
      <AutobiaButton size="medium" variant="contained">
        Medium
      </AutobiaButton>
      <AutobiaButton size="large" variant="contained">
        Large
      </AutobiaButton>
    </Stack>
  );

  const ButtonWithIconsExample = () => (
    <Stack direction="row" spacing={2} flexWrap="wrap">
      <AutobiaButton
        variant="contained"
        startIcon={<AddIcon />}
      >
        Add Item
      </AutobiaButton>
      <AutobiaButton
        variant="outlined"
        startIcon={<EditIcon />}
      >
        Edit
      </AutobiaButton>
      <AutobiaButton
        variant="contained"
        color="secondary"
        endIcon={<DownloadIcon />}
      >
        Download
      </AutobiaButton>
      <AutobiaButton
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
      >
        Delete
      </AutobiaButton>
    </Stack>
  );

  const ButtonStatesExample = () => (
    <Stack direction="row" spacing={2} flexWrap="wrap">
      <AutobiaButton variant="contained">
        Default
      </AutobiaButton>
      <AutobiaButton 
        variant="contained" 
        disabled
      >
        Disabled
      </AutobiaButton>
      <AutobiaButton 
        variant="contained"
        onClick={() => {
          setLoading(true);
          setTimeout(() => setLoading(false), 2000);
        }}
      >
        {loading ? 'Loading...' : 'Click Me'}
      </AutobiaButton>
    </Stack>
  );

  const IconButtonsExample = () => (
    <Stack direction="row" spacing={1}>
      <IconButton color="primary" size="small">
        <FavoriteIcon />
      </IconButton>
      <IconButton color="primary">
        <ShareIcon />
      </IconButton>
      <IconButton color="primary" size="large">
        <EditIcon />
      </IconButton>
      <IconButton color="error">
        <DeleteIcon />
      </IconButton>
    </Stack>
  );

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 6 }}>
        <Typography variant="h2" fontWeight={700} color="gray.g700">
          Buttons
        </Typography>
        <Typography variant="body1" color="gray.g600" sx={{ maxWidth: 600 }}>
          Button components with Autobia brand styling, multiple variants, sizes, 
          and states for all user interaction needs.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip label="Multiple Variants" color="primary" variant="outlined" />
          <Chip label="Icon Support" variant="outlined" />
          <Chip label="Responsive Sizes" variant="outlined" />
        </Stack>
      </Stack>

      <Stack spacing={6}>
        {/* Basic Buttons */}
        <LiveEditor
          title="Button Variants"
          description="Primary, secondary, outlined, and text button variants following Autobia brand guidelines."
          component={BasicButtonsExample}
          code={`import AutobiaButton from './components/Buttons/AutobiaButton';

const Example = () => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6} md={3}>
      <AutobiaButton variant="contained" color="primary" fullWidth>
        Primary Button
      </AutobiaButton>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <AutobiaButton variant="contained" color="secondary" fullWidth>
        Secondary Button
      </AutobiaButton>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <AutobiaButton variant="outlined" color="primary" fullWidth>
        Outlined Button
      </AutobiaButton>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <AutobiaButton variant="text" color="primary" fullWidth>
        Text Button
      </AutobiaButton>
    </Grid>
  </Grid>
);

// Props:
// variant: 'contained' | 'outlined' | 'text'
// color: 'primary' | 'secondary' | 'success' | 'error'
// fullWidth: boolean - Full width button`}
        />

        {/* Button Sizes */}
        <LiveEditor
          title="Button Sizes"
          description="Small, medium, and large button sizes for different UI contexts."
          component={ButtonSizesExample}
          code={`import AutobiaButton from './components/Buttons/AutobiaButton';

const Example = () => (
  <Stack direction="row" spacing={2} alignItems="center">
    <AutobiaButton size="small" variant="contained">
      Small
    </AutobiaButton>
    <AutobiaButton size="medium" variant="contained">
      Medium
    </AutobiaButton>
    <AutobiaButton size="large" variant="contained">
      Large
    </AutobiaButton>
  </Stack>
);

// Props:
// size: 'small' | 'medium' | 'large'`}
        />

        {/* Buttons with Icons */}
        <LiveEditor
          title="Buttons with Icons"
          description="Buttons with start and end icons for enhanced visual communication."
          component={ButtonWithIconsExample}
          code={`import AutobiaButton from './components/Buttons/AutobiaButton';
import { AddIcon, EditIcon, DownloadIcon, DeleteIcon } from '@mui/icons-material';

const Example = () => (
  <Stack direction="row" spacing={2}>
    <AutobiaButton
      variant="contained"
      startIcon={<AddIcon />}
    >
      Add Item
    </AutobiaButton>
    <AutobiaButton
      variant="outlined"
      startIcon={<EditIcon />}
    >
      Edit
    </AutobiaButton>
    <AutobiaButton
      variant="contained"
      color="secondary"
      endIcon={<DownloadIcon />}
    >
      Download
    </AutobiaButton>
    <AutobiaButton
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
    >
      Delete
    </AutobiaButton>
  </Stack>
);

// Props:
// startIcon: ReactNode - Icon at the start of button
// endIcon: ReactNode - Icon at the end of button`}
        />

        {/* Button States */}
        <LiveEditor
          title="Button States"
          description="Default, disabled, and loading states for different interaction scenarios."
          component={ButtonStatesExample}
          code={`import AutobiaButton from './components/Buttons/AutobiaButton';

const [loading, setLoading] = useState(false);

const Example = () => (
  <Stack direction="row" spacing={2}>
    <AutobiaButton variant="contained">
      Default
    </AutobiaButton>
    <AutobiaButton 
      variant="contained" 
      disabled
    >
      Disabled
    </AutobiaButton>
    <AutobiaButton 
      variant="contained"
      onClick={() => {
        setLoading(true);
        setTimeout(() => setLoading(false), 2000);
      }}
    >
      {loading ? 'Loading...' : 'Click Me'}
    </AutobiaButton>
  </Stack>
);

// Props:
// disabled: boolean - Disable button interaction
// onClick: function - Click handler`}
        />

        {/* Icon Buttons */}
        <LiveEditor
          title="Icon Buttons"
          description="Compact icon-only buttons for actions where space is limited."
          component={IconButtonsExample}
          code={`import { IconButton } from '@mui/material';
import { FavoriteIcon, ShareIcon, EditIcon, DeleteIcon } from '@mui/icons-material';

const Example = () => (
  <Stack direction="row" spacing={1}>
    <IconButton color="primary" size="small">
      <FavoriteIcon />
    </IconButton>
    <IconButton color="primary">
      <ShareIcon />
    </IconButton>
    <IconButton color="primary" size="large">
      <EditIcon />
    </IconButton>
    <IconButton color="error">
      <DeleteIcon />
    </IconButton>
  </Stack>
);

// IconButton is from Material-UI
// size: 'small' | 'medium' | 'large'
// color: 'primary' | 'secondary' | 'error' | 'success'`}
        />
      </Stack>

      {/* Usage Guidelines */}
      <Box sx={{ mt: 8, p: 4, backgroundColor: 'gray.g25', borderRadius: 2 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Usage Guidelines
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Button Hierarchy
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Use contained buttons for primary actions, outlined for secondary actions, 
              and text buttons for tertiary actions.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Color Usage
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Primary (orange) for main actions, Secondary (blue) for supporting actions, 
              Error (red) for destructive actions.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Accessibility
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Ensure sufficient color contrast and provide clear labels. 
              Use icons with text when possible.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Responsive Design
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Use fullWidth prop on mobile screens and appropriate sizes 
              for different screen densities.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Buttons;